import { message } from "antd";
import _ from "lodash";
import { Dispatch } from "redux";

import { handBookApi } from "./api";
import { HANDBOOK_TEMPLATE_LVL, HandbookExpenditureLocalChanges, handBookActions } from "./slice";
import { RootState } from "app/store/rootReducer";

import {
  HANDBOOK_EXPENDITURE_APPROVAL_STATUS,
  ICreateManualExpenditure,
  ICreateTemplateExpenditure,
  ICreateTemplateSection,
  IHandBook,
  IHandBookRequest,
  IHandBookSegment,
  IHandBookTemplate,
  IHandBookTemplateExpenditure,
  IMaterialInHandbookExpenditure,
} from "./types";

import { ICreateLinkedSegments, createLinkedSegments, transformFormValuesToLinkedSegment } from "../utils/segments";
import { errorCatcher } from "utils/helpers/errorCatcher";
import { generateStorageKey } from "utils/helpers/generateStorageKey";

export const getHandbooks =
  (
    { limit, offset, ...filter }: IHandBookRequest = { status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED },
    controller: AbortController
  ) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    //TODO поправить типы generateStorageKey
    const key = generateStorageKey(filter as any);
    if (!getState().handBookReducer.handbooks[key]) {
      dispatch(handBookActions.setIsLoading({ key, status: true }));
    }
    handBookApi
      .getList({ limit, offset, ...filter }, controller)
      .then(({ data }) => {
        dispatch(handBookActions.setItems({ key, items: data }));
      })
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key, status: false }));
      });
  };

export const getHandbooksMore =
  (filters: IHandBookRequest = { status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const loadingKey = generateStorageKey(filters as any);
    dispatch(handBookActions.setIsLoading({ key: loadingKey, status: true })); // Блокируем кнопку "Показать ещё"
    const { limit, offset, ...pushKeyObject } = filters;
    //TODO поправить типы generateStorageKey
    const pushKey = generateStorageKey(pushKeyObject as any);
    if (!getState().handBookReducer.handbooks[pushKey]) {
      dispatch(handBookActions.setIsLoading({ key: pushKey, status: true }));
    }
    handBookApi
      .getList(filters)
      .then(({ data }) => {
        dispatch(handBookActions.setItemsMore({ key: pushKey, items: data }));
      })
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key: pushKey, status: false }));
        dispatch(handBookActions.setIsLoading({ key: loadingKey, status: false }));
      });
  };

export const getHandbooksAggregate = () => (dispatch: Dispatch) => {
  dispatch(handBookActions.setIsLoading({ key: "getHandbooksAggregate", status: true }));
  handBookApi
    .getAggregate()
    .then(({ data }) => {
      dispatch(handBookActions.setAggregate(data));
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: "getHandbooksAggregate", status: false }));
    });
};

export const editHandbook = (id: number, data: any) => (dispatch: Dispatch) => {
  const key = generateStorageKey({ id, type: "mutate" });
  dispatch(handBookActions.setIsLoading({ key, status: true }));
  return handBookApi
    .patch(id, data)
    .then(({ data }) => {
      dispatch(handBookActions.updateItem(data));
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const deleteHandbook = (id: number) => (dispatch: Dispatch) => {
  const key = generateStorageKey({ id, type: "mutate" });
  dispatch(handBookActions.setIsLoading({ key, status: true }));
  return handBookApi
    .delete(id)
    .then(({ data }) => {
      dispatch(handBookActions.deleteItem(id));
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const cancelEditingHandbook = () => (dispatch: Dispatch) => {
  dispatch(handBookActions.clearEditings());
  dispatch(handBookActions.setEditing(false));
};

export const cancelEditingHandbookTemplates = () => (dispatch: Dispatch) => {
  dispatch(handBookActions.clearTemplateEditings());
  dispatch(handBookActions.setEditingTemplates(false));
};

export const applyEditingHandbook = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const handbookEditings = getState().handBookReducer.editings;
  // @ts-ignore
  const deletions = Object.keys(handbookEditings.deletions).map((id) => dispatch(deleteHandbook(+id)));
  const editings = Object.entries(handbookEditings.changes).map(([id, change]) =>
    // @ts-ignore | Пропускаем patch удалённых строк
    handbookEditings.deletions[+id] ? Promise.resolve() : dispatch(editHandbook(+id, change))
  );
  await Promise.allSettled([...deletions, ...editings]);
  message.success("Справочник отредактирован");
  dispatch(handBookActions.clearEditings());
  dispatch(handBookActions.setEditing(false));
  dispatch(handBookActions.invalidateKey());
};

export const applyEditingHandbookTemplates = () => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch(handBookActions.setIsLoading({ key: "applyEditingHandbookTemplates", status: true }));
  const handbookTemplateEditings = getState().handBookReducer.templateEditings;
  // Шаблоны – delete
  const templateDeletions = Object.keys(handbookTemplateEditings.templates.deletions).map((id) =>
    handBookApi.deleteTemplate(+id).catch(errorCatcher)
  );
  // Шаблоны – patch
  const templateEditings = Object.entries(handbookTemplateEditings.templates.changes).map(([id, change]) =>
    // Пропускаем patch удалённых строк
    handbookTemplateEditings.templates.deletions[+id]
      ? Promise.resolve()
      : handBookApi.patchTemplate(+id, change).catch(errorCatcher)
  );
  // Секции – delete
  const sectionDeletions = Object.keys(handbookTemplateEditings.sections.deletions).map((id) =>
    handBookApi.deleteTemplateSection(id).catch(errorCatcher)
  );
  // Секции – patch
  const sectionEditings = Object.entries(handbookTemplateEditings.sections.changes).map(([id, change]) =>
    // Пропускаем patch удалённых строк
    handbookTemplateEditings.sections.deletions[+id]
      ? Promise.resolve()
      : handBookApi.patchTemplateSection(+id, change).catch(errorCatcher)
  );
  // Расценки – delete
  let expenditureDeletions = Promise.resolve({});
  const expToDelete = Object.keys(handbookTemplateEditings.expenditures.deletions)
    .map((id) => +id)
    .filter((id) => id > 0);
  if (expToDelete.length) {
    // @ts-ignore
    expenditureDeletions = handBookApi.bulkDeleteTemplateExpenditure(expToDelete).catch(errorCatcher);
  }
  // Расценки – создание атрибутов
  const existingChanges = Object.entries(handbookTemplateEditings.expenditures.changes).filter(
    ([id, _]) =>
      // Пропускаем patch удалённых строк
      !handbookTemplateEditings.expenditures.deletions[+id]
  );
  const rawLinkedSegments = existingChanges
    .map(([id, changes]) => {
      if (
        changes.expenditure?.template?.segment !== undefined ||
        // @ts-ignore
        changes.expenditure?.template?.parent_segment !== undefined
      ) {
        return transformFormValuesToLinkedSegment(changes.expenditure.template!, +id);
      }
    })
    .filter((x) => x) as ICreateLinkedSegments[];

  const createdSegmentsRecord = await createLinkedSegments(
    getState().handBookReducer.allSegments,
    rawLinkedSegments,
    (data) =>
      handBookApi
        .createSegment(data)
        .then(({ data }) => data)
        .catch(errorCatcher)
  );
  async function createNewTemplateExpenditure({
    handbook,
    template,
  }: {
    /** id расценки по Справочнику (выбор через автокомплит) или данные для создания новой расценки в Справочнике */
    handbook?: ICreateManualExpenditure | number;
    /** Данные расценки в шаблоне – цена, кол-во, атрибуты */
    template: Omit<ICreateTemplateExpenditure, "manual_expenditure_id">;
  }) {
    if (typeof handbook === "object") {
      if (!handbook.name || !handbook.measure) {
        return;
      }
    }
    const expenditureIdFromHandbookId =
      typeof handbook === "number"
        ? handbook
        : handbook &&
          (await handBookApi
            .post(handbook)
            .then(({ data }) => data.id)
            .catch(errorCatcher));
    return handBookApi
      .createTemplateExpenditure({ ...template, manual_expenditure_id: expenditureIdFromHandbookId! })
      .then(({ data }) => data)
      .catch(errorCatcher);
  }
  async function cudTemplateMaterials(
    templateExpenditureId: number,
    {
      newMaterials,
      editedMaterials,
      deletedMaterials,
    }: {
      newMaterials?: Array<{
        /** id ресурса по Справочнику (выбор через автокомплит) или данные для создания нового ресурса в Справочнике */
        handbook?: ICreateManualExpenditure | number;
        /** Данные ресурса в шаблоне – цена, кол-во, атрибуты */
        template: Omit<ICreateTemplateExpenditure, "manual_expenditure_id" | "related_work_id">;
      }>;
      editedMaterials?: Array<{
        handbook?: ICreateManualExpenditure | number;
        template: IHandBookTemplateExpenditure & { manual_expenditure: { id: number } };
      }>;
      deletedMaterials?: number[];
    }
  ) {
    let promises: Promise<any>[] = [];
    if (newMaterials && newMaterials.length > 0) {
      promises = promises.concat(
        newMaterials.map(
          (material) =>
            new Promise<void>(async (resolve, reject) => {
              if (typeof material.handbook === "object") {
                if (!material.handbook.name || !material.handbook.measure) {
                  reject();
                }
              }
              const attachId =
                typeof material.handbook === "number"
                  ? material.handbook
                  : material.handbook &&
                    (await handBookApi
                      .post(material.handbook)
                      .then(({ data }) => data.id)
                      .catch(errorCatcher));
              // @ts-ignore
              const { manual_expenditure, id, ...templatePayload } = material.template || {};
              await handBookApi.createTemplateExpenditure({
                ...templatePayload,
                manual_expenditure_id: attachId!,
                related_work_id: templateExpenditureId,
              });
              resolve();
            })
        )
      );
    }
    if (editedMaterials && editedMaterials.length > 0) {
      promises = promises.concat(
        editedMaterials
          .filter((m) => !deletedMaterials?.includes(m.template.id))
          .map(
            (material) =>
              new Promise<void>(async (resolve) => {
                let materialId = material.template.manual_expenditure.id;
                if (material.handbook) {
                  materialId =
                    typeof material.handbook === "number"
                      ? material.handbook
                      : (material.handbook &&
                          (await handBookApi
                            .post(material.handbook)
                            .then(({ data }) => data.id)
                            .catch(errorCatcher)))!;
                }
                await handBookApi.patchTemplateExpenditure(material.template.id, {
                  ...material.template,
                  manual_expenditure_id: materialId,
                });
                resolve();
              })
          )
      );
    }
    if (deletedMaterials && deletedMaterials.length > 0) {
      promises.push(handBookApi.bulkDeleteTemplateExpenditure(deletedMaterials));
    }
    return Promise.all(promises);
  }
  async function editTemplateExpenditure(
    templateExpenditureId: number,
    { handbook, template }: Partial<Parameters<typeof createNewTemplateExpenditure>[0]>,
    { newMaterials, editedMaterials, deletedMaterials }: Parameters<typeof cudTemplateMaterials>[1]
  ) {
    if (handbook) {
      const expenditureIdFromHandbookId =
        typeof handbook === "number"
          ? handbook
          : handbook &&
            (await handBookApi
              .post(handbook)
              .then(({ data }) => data.id)
              .catch(errorCatcher));
      await handBookApi.patchTemplateExpenditure(templateExpenditureId, {
        manual_expenditure_id: expenditureIdFromHandbookId!,
      });
    }
    if (template && Object.keys(template).length > 0) {
      await handBookApi.patchTemplateExpenditure(templateExpenditureId, template);
    }
    return cudTemplateMaterials(templateExpenditureId, { newMaterials, editedMaterials, deletedMaterials });
  }
  // Расценки – post
  // Новые расценки (создаём последовательно)
  const createdExpendituresIdsMap = new Map<number, number>();
  for (const mockExpenditure of handbookTemplateEditings.expenditures.new) {
    const mockExpenditureChanges = handbookTemplateEditings.expenditures.changes[mockExpenditure.id];
    // Пропускаем создание незаполненных строк шаблона
    if (!mockExpenditureChanges?.expenditure?.template && !mockExpenditureChanges?.expenditure?.handbook) {
      continue;
    }
    const createdTemplateExpenditure = await createNewTemplateExpenditure({
      handbook: mockExpenditureChanges.expenditure?.handbook,
      // Применяет атрибуты
      template: Object.assign({}, mockExpenditureChanges.expenditure?.template || {}, {
        segment: createdSegmentsRecord[Number(mockExpenditure.id)]?.segment?.id,
        section_id: mockExpenditure.section_id,
      }),
    });

    if (createdTemplateExpenditure?.id) {
      createdExpendituresIdsMap.set(Number(mockExpenditure.id), createdTemplateExpenditure.id);
    }
  }
  // Расценки и связанные ресурсы
  const expenditureEditings = existingChanges.flatMap(
    ([_id, changes]: [string, Partial<HandbookExpenditureLocalChanges>]) => {
      let promises: Promise<any>[] = [];
      const createdExpenditureId = createdExpendituresIdsMap.get(Number(_id));
      const id = createdExpenditureId || Number(_id);
      const { expenditure, newMaterials, editedMaterials, deletedMaterials } = changes;
      if (createdExpenditureId) {
        // Для только что созданных расценок только прикрепляем материалы
        promises.push(cudTemplateMaterials(createdExpenditureId, { newMaterials, editedMaterials, deletedMaterials }));
      } else {
        promises.push(
          editTemplateExpenditure(
            id,
            {
              handbook: expenditure?.handbook,
              template: expenditure?.template && {
                ...expenditure.template,
                // Атрибуты записываются в createdSegmentsRecord до объявления replacedIdMap
                segment: createdSegmentsRecord[Number(_id)]?.segment?.id,
              },
            },
            { newMaterials, editedMaterials, deletedMaterials }
          )
        );
      }
      return promises;
    }
  );
  // Атрибуты – delete
  const segmentDeletions = Object.keys(handbookTemplateEditings.segments.deletions).map((id) =>
    handBookApi.deleteSegment(id).catch(errorCatcher)
  );
  // Атрибуты – patch
  const segmentEditings = Object.entries(handbookTemplateEditings.segments.changes).map(([id, change]) =>
    // Пропускаем patch удалённых строк
    handbookTemplateEditings.segments.deletions[+id]
      ? Promise.resolve()
      : handBookApi.patchSegment(+id, change).catch(errorCatcher)
  );

  await Promise.allSettled([
    ...templateDeletions,
    ...templateEditings,
    ...sectionDeletions,
    ...sectionEditings,
    ...segmentDeletions,
    ...segmentEditings,
    expenditureDeletions,
    ...expenditureEditings,
  ]);
  message.success("Редактирование завершено");
  dispatch(handBookActions.clearTemplateEditings());
  dispatch(handBookActions.setEditing(false));
  dispatch(handBookActions.invalidateKey());
  dispatch(handBookActions.setIsLoading({ key: "applyEditingHandbookTemplates", status: false }));
};

export const localChangeHandbookTemplate =
  (id: number, change: Partial<IHandBookTemplate>, lvl: HANDBOOK_TEMPLATE_LVL) => (dispatch: Dispatch) => {
    dispatch(handBookActions.localChangeTemplateItem({ id, change, lvl }));
  };

export const localDeleteHandbook = (id: number) => (dispatch: Dispatch) => {
  dispatch(handBookActions.localDeleteItem(id));
};

export const localChangeHandbook = (id: number, change: Partial<IHandBook>) => (dispatch: Dispatch) => {
  dispatch(handBookActions.localChangeItem({ id, change }));
};

export const localDeleteCheckedHandbooks = () => (dispatch: Dispatch, getState: () => RootState) => {
  const checkedHandbooks = getState().handBookReducer.editings.checked;
  Object.entries(checkedHandbooks).forEach(([handbookId, isChecked]) => {
    if (isChecked) {
      dispatch(handBookActions.localDeleteItem(+handbookId));
      dispatch(handBookActions.localCheckItem({ id: +handbookId, isChecked: false }));
    }
  });
};

export const localDeleteCheckedHandbookTemplates = () => (dispatch: Dispatch, getState: () => RootState) => {
  const editings = getState().handBookReducer.templateEditings;
  const lvls = [
    { lvl: HANDBOOK_TEMPLATE_LVL.TEMPLATES, checked: editings.templates.checked },
    { lvl: HANDBOOK_TEMPLATE_LVL.SECTIONS, checked: editings.sections.checked },
    { lvl: HANDBOOK_TEMPLATE_LVL.EXPENDITURES, checked: editings.expenditures.checked },
    { lvl: HANDBOOK_TEMPLATE_LVL.SEGMENTS, checked: editings.segments.checked },
  ];
  const tepmplateExpendituresMaterialIdsMap = new Map<number, number[]>(
    (
      getState().handBookReducer.templateExpenditures[getState().handBookReducer._lastUsedKeyTemplateExpenditures]
        ?.results || []
    ).map((expenditure: IHandBookTemplateExpenditure) => [
      expenditure.id,
      expenditure.materials.map((material) => material.id),
    ])
  );
  lvls.forEach(({ lvl, checked }) => {
    Object.entries(checked).forEach(([id, isChecked]) => {
      if (isChecked) {
        dispatch(handBookActions.localDeleteTemplateItem({ id: +id, lvl }));
        if (lvl === HANDBOOK_TEMPLATE_LVL.EXPENDITURES) {
          const materialsOfDeletingExpenditure = tepmplateExpendituresMaterialIdsMap.get(+id);
          if (materialsOfDeletingExpenditure?.length) {
            dispatch(handBookActions.localBulkDeleteTemplateItem({ lvl, ids: materialsOfDeletingExpenditure }));
          }
        }
        dispatch(handBookActions.localCheckTemplateItem({ id: +id, isChecked: false, lvl }));
      }
    });
  });
};

export const cloneCheckedHandbookSections =
  ({ templateId, sectionId }: { templateId: string; sectionId?: string }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const editings = getState().handBookReducer.templateEditings;
    if (sectionId) {
      // Подсекции
      const checkedIds = Object.entries(editings.sections.checked)
        .filter(([_, isChecked]) => isChecked)
        .map(([id, _]) => ({ subsection_id: +id, parent_id: +sectionId }));
      if (!checkedIds.length) return;
      return handBookApi
        .cloneTemplateSubSections({ templateId, ids: checkedIds })
        .catch(errorCatcher)
        .then(() => {
          dispatch(handBookActions.invalidateKey());
          message.success("Подразделы скопированы");
        });
    } else {
      // Секции
      const checkedIds = Object.entries(editings.sections.checked)
        .filter(([_, isChecked]) => isChecked)
        .map(([id, _]) => ({ section_id: +id }));
      if (!checkedIds.length) return;
      return handBookApi
        .cloneTemplateSections({ templateId, ids: checkedIds })
        .catch(errorCatcher)
        .then(() => {
          dispatch(handBookActions.invalidateKey());
          message.success("Разделы скопированы");
        });
    }
  };

export const cloneCheckedHandbookSegments =
  ({ templateId }: { templateId: string }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const editings = getState().handBookReducer.templateEditings;
    const checkedIds = Object.entries(editings.segments.checked)
      .filter(([_, isChecked]) => isChecked)
      .map(([id, _]) => ({ segment_id: +id }));
    if (!checkedIds.length) return;
    return handBookApi
      .cloneTemplateSegments({ templateId, ids: checkedIds })
      .then(({ data }) => {
        dispatch(handBookActions.pushSegments(data.filter((s: IHandBookSegment) => Array.isArray(s.children))));
        dispatch(handBookActions.invalidateKey());
        message.success("Атрибуты скопированы");
      })
      .catch(errorCatcher);
  };

export const cloneCheckedHandbookExpenditures =
  ({ subSectionId }: { subSectionId: string | undefined }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const editings = getState().handBookReducer.templateEditings.expenditures;
    const expenditures =
      getState().handBookReducer.templateExpenditures[getState().handBookReducer._lastUsedKeyTemplateExpenditures];
    expenditures.results?.forEach((expenditure) => {
      if (!editings.checked[expenditure.id]) return;
      const cloneId = -Date.now();
      const changesClone = _.cloneDeep(editings.changes[expenditure.id] || {});
      // Дублируем изменения для скопированной расценки
      dispatch(
        handBookActions.localChangeTemplateItem({
          id: cloneId,
          lvl: HANDBOOK_TEMPLATE_LVL.EXPENDITURES,
          change: {
            ...changesClone,
            expenditure: {
              handbook: changesClone?.expenditure?.handbook || expenditure.manual_expenditure.id,
              template: Object.assign({}, changesClone?.expenditure?.template || expenditure, {
                section_id: subSectionId ? +subSectionId : expenditure.section_id,
              }),
            },
            newMaterials: (
              expenditure.materials?.map((m) => ({
                handbook: m.manual_expenditure.id,
                template: Object.assign({}, m, { section_id: subSectionId ? +subSectionId : expenditure.section_id }),
              })) || []
            )
              // @ts-ignore
              .concat(changesClone.newMaterials || []),
          },
        })
      );
      dispatch(
        handBookActions.localAddTemplateItem({
          lvl: HANDBOOK_TEMPLATE_LVL.EXPENDITURES,
          item: {
            ..._.cloneDeep(expenditure),
            id: cloneId,
            _originalId: expenditure.id,
            section_id: subSectionId ? +subSectionId : expenditure.section_id,
            materials: (expenditure.materials || []).concat(
              // @ts-ignore
              changesClone.newMaterials?.map((m) => ({ ...m.template, manual_expenditure: m.handbook })) || []
            ),
          },
        })
      );
    });
  };

export const createHandbookItem =
  (vals: ICreateManualExpenditure & { id: number }) => (dispatch: Dispatch, getState: () => RootState) => {
    const key = getState().handBookReducer._lastUsedKey;
    const { id: mockId, ...payload } = vals;
    return handBookApi
      .post(payload)
      .then(({ data }) => {
        dispatch(handBookActions.addItem({ item: data, key }));
        dispatch(handBookActions.localDeleteAddingItem(mockId!));
        message.success("Запись успешно добавлена");
      })
      .catch(errorCatcher);
  };

export const createHandTemplate = (vals: any, onSuccess?: () => void) => (dispatch: Dispatch) => {
  dispatch(handBookActions.setIsLoading({ key: "createTemplate", status: true }));
  handBookApi
    .createTemplate(vals)
    .then(({ data }) => {
      dispatch(handBookActions.pushTemplate(data));
      onSuccess?.();
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: "createTemplate", status: false }));
    });
};

export const getHandbookTemplates = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter);
  dispatch(handBookActions.setIsLoading({ key, status: true }));
  handBookApi
    .getTemplatesList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplates({ key, templates: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const getHandbookTemplatesMore = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey({ ...restFilter });
  const loadingKey = generateStorageKey({ ...restFilter, type: "moreTemplates" });
  if (!getState().handBookReducer.templates[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key: loadingKey, status: true }));
  }
  handBookApi
    .getTemplatesList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplatesMore({ key, templates: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: loadingKey, status: false }));
    });
};

export const approveHandbookTemplate = (id: number, onSuccess?: () => void) => (dispatch: Dispatch) => {
  const key = generateStorageKey({ id, type: "approveHandbookTemplate" });

  dispatch(handBookActions.setIsLoading({ key, status: true }));
  handBookApi
    .approveTemplate({ id })
    .then(() => {
      dispatch(handBookActions.invalidateKey());
      onSuccess?.();
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const createHandbookTemplateSection =
  (vals: ICreateTemplateSection, onSuccess?: () => void) => (dispatch: Dispatch) => {
    dispatch(handBookActions.setIsLoading({ key: "createTemplateSection", status: true }));
    handBookApi
      .createTemplateSection(vals)
      .then(() => {
        dispatch(handBookActions.invalidateKey());
        onSuccess?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key: "createTemplateSection", status: false }));
      });
  };

export const getHandbookTemplateSections = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter);
  if (!getState().handBookReducer.templateSections[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key, status: true }));
  }
  handBookApi
    .getTemplateSectionsList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplateSections({ key, templateSections: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const getHandbookTemplateSectionsMore = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey({ ...restFilter });
  const loadingKey = generateStorageKey({ ...restFilter, type: "moreTemplateSections" });
  if (!getState().handBookReducer.templateSections[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key: loadingKey, status: true }));
  }
  handBookApi
    .getTemplateSectionsList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplateSectionsMore({ key, templateSections: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: loadingKey, status: false }));
    });
};

export const getHandbookTemplateExpenditures = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter);
  if (!getState().handBookReducer.templateExpenditures[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key, status: true }));
  }
  handBookApi
    .getTemplateExpendituresList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplateExpenditures({ key, templateExpenditures: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const getHandbookTemplateExpendituresMore = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey({ ...restFilter });
  const loadingKey = generateStorageKey({ ...restFilter, type: "moreTemplateExpenditures" });
  if (!getState().handBookReducer.templateExpenditures[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key: loadingKey, status: true }));
  }
  handBookApi
    .getTemplateExpendituresList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplateExpendituresMore({ key, templateExpenditures: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: loadingKey, status: false }));
    });
};

export const addCheckedExpendituresToTemplate =
  ({ sectionId, successCallback }: { sectionId: string; successCallback?: () => void }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const checkedHandbooks = getState().handBookReducer.editings.checked;
    const changes = getState().handBookReducer.editings.changes;
    const lastUsedKey = getState().handBookReducer._lastUsedKeyTemplateExpenditures;
    const allExpendituresMap = new Map(
      (getState().handBookReducer.handbooks[lastUsedKey]?.results || []).map((x) => [x.id, x])
    );
    dispatch(handBookActions.setIsLoading({ key: "addCheckedExpendituresToTemplate", status: true }));
    await handBookApi
      .bulkCreateTemplateExpenditure(
        Object.entries(checkedHandbooks)
          .filter(([_, isChecked]) => isChecked)
          .map(([handbookId, _]) => ({
            section_id: +sectionId,
            manual_expenditure_id: +handbookId,
            count: changes[+handbookId]?.count || "0",
            price: allExpendituresMap.get(+handbookId)?.price || "0",
          }))
      )
      .then(({ data }) => {
        message.success("Работы добавлены в шаблон");
        // очистка количества и стоимости в реестре работ в модалке
        dispatch(handBookActions.clearEditings());
        // добавляем работы без перезагрузки и очистки редактирования
        dispatch(handBookActions.pushTemplateExpenditures(data));
        successCallback?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key: "addCheckedExpendituresToTemplate", status: false }));
      });
  };

export const templateExpendituresLocalEdit =
  ({
    expenditureId,
    ...change
  }: HandbookExpenditureLocalChanges & {
    expenditureId: number;
  }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(
      handBookActions.localChangeTemplateItem({ id: expenditureId, lvl: HANDBOOK_TEMPLATE_LVL.EXPENDITURES, change })
    );
  };

export const sendTemplate =
  ({
    objectId,
    templateId,
    finallyCallback,
    successCallback,
  }: {
    objectId: string;
    templateId: number;
    finallyCallback?: () => void;
    successCallback?: (data: { task_id?: string }) => void;
  }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const key = generateStorageKey({ type: "send", id: templateId, objectId });
    dispatch(handBookActions.setIsLoading({ status: true, key }));
    handBookApi
      .sendTemplate({ id: templateId, buildingId: objectId })
      .then(({ data }) => {
        successCallback?.(data);
      })
      .catch(errorCatcher)
      .finally(() => {
        finallyCallback?.();
        dispatch(handBookActions.setIsLoading({ status: false, key }));
      });
  };

export const approveCheckedExpenditures =
  ({ successCallback }: { successCallback?: () => void }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const checkedExpenditures = getState().handBookReducer.editings.checked;
    const checkedIds = Object.entries(checkedExpenditures)
      .filter(([_, isChecked]) => isChecked)
      .map(([id, _]) => +id);
    if (!checkedIds.length) return;
    dispatch(handBookActions.setIsLoading({ key: "approveCheckedExpenditures", status: true }));
    await handBookApi
      .approveExpenditures({
        ids: checkedIds,
        status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED,
      })
      .then(() => {
        message.success("Позиции согласованы");
        dispatch(handBookActions.clearTemplateEditings());
        dispatch(handBookActions.invalidateKey());
        successCallback?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key: "approveCheckedExpenditures", status: false }));
      });
  };

export const checkAllHandbookExpenditures = (isChecked: boolean) => (dispatch: Dispatch, getState: () => RootState) => {
  const lastUsedKey = getState().handBookReducer._lastUsedKey;
  const handbooks = getState().handBookReducer.handbooks[lastUsedKey];
  const allCheckeds = Object.fromEntries(handbooks.results.map((item) => [item.id, isChecked]));
  dispatch(handBookActions.localReplaceChecked(allCheckeds));
};

export const getHandbooksSegments = (templateId?: string) => (dispatch: Dispatch, getState: () => RootState) => {
  const segments = getState().handBookReducer.segments;
  if (!segments?.length) {
    dispatch(handBookActions.setIsLoading({ key: "getHandbooksSegments", status: true }));
  }
  handBookApi
    .getSegments(templateId ? { template_id: templateId } : undefined)
    .then(({ data }) => {
      dispatch(handBookActions.setSegments(data.results));
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: "getHandbooksSegments", status: false }));
    });
};

export const getHandbooksAllSegments = () => (dispatch: Dispatch, getState: () => RootState) => {
  const allSegments = getState().handBookReducer.allSegments;
  if (!allSegments?.length) {
    dispatch(handBookActions.setIsLoading({ key: "getHandbooksAllSegments", status: true }));
  }
  handBookApi
    .getSegments()
    .then(({ data }) => {
      dispatch(handBookActions.setAllSegments(data.results));
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: "getHandbooksSegments", status: false }));
    });
};
